import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ErrorView } from '@percent/partner-dashboard/common/components'
import styles from './NonprofitsTable.module.scss'
import { useAuthState } from '@percent/partner-dashboard/common/hooks'
import { NonprofitsListTypes, NonprofitsTableProps } from './NonprofitsTable.types'
import defaultLogo from '@percent/partner-dashboard/common/assets/images/default-img.png'
import { formatAmount, formatMinorUnitsValueToFullNumberWithDecimals } from '@percent/utility'
import {
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableColumnSortDirection,
  TablePagination,
  TableProvider,
  TableRow
} from '@percent/lemonade'

export function NonprofitsTable({
  data,
  isLoading,
  totalResults,
  errorMessage,
  nextPage,
  previousPage,
  query
}: NonprofitsTableProps) {
  const { t } = useTranslation()

  const [sortDirection, setSortDirection] = useState<TableColumnSortDirection>()
  const [sortByColumn, setSortByColumn] = useState<string>('')

  const { currencyInfo } = useAuthState()

  const handleRequestSort = (columnKey: string) => {
    const isAsc = sortByColumn === columnKey && sortDirection === 'ASC'
    setSortDirection(isAsc ? 'DESC' : 'ASC')
    setSortByColumn(columnKey)

    const newQueryParams = {
      direction: isAsc ? 'DESC' : 'ASC',
      sort: columnKey
    }
    query(newQueryParams)
  }

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  const columns: TableColumn[] = [
    { id: 'name', content: t('table.header.name'), isSortable: true },
    {
      id: 'amount',
      content: t('table.header.raised'),
      props: { width: '5%', textAlign: 'right' },
      isSortable: true
    }
  ]

  return (
    <TableProvider
      columns={columns}
      title={t('menu.nonprofits')}
      totalResults={totalResults}
      isLoading={isLoading}
      illustration="no-results"
      emptyTableTitle={t('typography.noRequestsTable')}
      handleRequestSort={handleRequestSort}
      sortDirection={sortDirection}
      sortByColumn={sortByColumn}
    >
      <Table>
        <TableBody>
          {data?.map(({ id, logo, name, amount, currencyCode }: NonprofitsListTypes) => (
            <TableRow key={id}>
              <TableCell dataTestId="nonprofits-name" className={styles.logoName}>
                <Avatar image={logo || defaultLogo} size="medium" alt={`${name} logo`} />
                {name}
              </TableCell>
              <TableCell dataTestId="nonprofits-amount" inlineStyle={{ textAlign: 'right' }}>
                {currencyCode &&
                  formatAmount({
                    currencyCode,
                    value: formatMinorUnitsValueToFullNumberWithDecimals(
                      amount,
                      currencyInfo?.find(el => el.code === currencyCode)?.minorUnits as number
                    )
                  })}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        totalResultsText={t('table.results', { count: totalResults || undefined })}
        previousPage={previousPage}
        nextPage={nextPage}
      />
    </TableProvider>
  )
}
